import { useEffect, useCallback, useState, useRef } from "react";
import { debounce } from "src/utils";
import { useHistory } from 'react-router-dom';

// An effect that returns a debounced function every time the delay is changed
export const useDebounce = (fn, delay, watch = []) => useCallback(debounce(fn, delay), [delay, ...watch]);

// Checks and returns a sampled scroll amount
export const useScrollAmount = (target, footerStickyOffset = 0, sampleRate = 50) => {
  const [scrolled, setScrolled] = useState(0); // The scroll amount

  const handleScroll = debounce(() => {
    const newScrolled = target.scrollTop;

    if (newScrolled > footerStickyOffset && scrolled - 1 < footerStickyOffset) {
      setScrolled(newScrolled);
    } else if (newScrolled - 1 < footerStickyOffset && scrolled > footerStickyOffset) {
      setScrolled(newScrolled);
    } else if (newScrolled + window.outerHeight == target.clientHeight) {
      setScrolled(newScrolled);
    }
  }, sampleRate);

  useEventCb("scroll", handleScroll);

  return scrolled;
};

export const useDisabledBodyScroll = (condition = true) => {
  useEffect(() => {
    condition && (document.body.style.overflow = "hidden");
    return () => {
      condition && (document.body.style.overflow = "unset");
    };
  }, [condition]);
};

// An effect that calls a given function everytime an event is fired
export const useEventCb = (event, cb) =>
  useEffect(() => {
    document.addEventListener(event, cb);
    return () => {
      document.removeEventListener(event, cb);
    };
  }, [event, cb]);

export const useOnce = (fn) => {
    const val = useRef(false);
    return (...args) => !val.current && (val.current = fn(...args) || true)
  }

// handle browser back and forward native commands
export const useBrowserNav = (fn, [...deps]) => {
  const [locationKeys, setLocationKeys] = useState([]);
  const history = useHistory();

  useEffect(() => {
    return history.listen(location => {
      if (history.action === "PUSH") {
        setLocationKeys([ location.key ])
      }

      if (history.action === "POP") {
        if (locationKeys[1] === location.key) {
          setLocationKeys(([ _, ...keys ]) => keys) // eslint-disable-line
          fn(location);
        } else {
          setLocationKeys((keys) => [ location.key, ...keys ])
          fn(location);
        }
      }
    })
  }, [locationKeys, ...deps])
}

export const useResizeObserver = (selector, property = 'height') => {
  const [prop, setProp] = useState();

  useEffect(() => {
    const el = document.querySelector(selector);
    if (!el) return;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(entry => {
        setProp(entry.contentRect[property])
      });
    });
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect(); // clean up 
  }, []);

  return prop
}

export const useMousePosition = ({ active = false }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (active) {
      const updateMousePosition = e => {
        if (e.type.startsWith("touch")) {
          // For touch events
          const touch = e.touches[0] || e.changedTouches[0];
          setPosition({ x: touch.clientX, y: touch.clientY });
        } else {
          // For mouse events
          setPosition({ x: e.clientX, y: e.clientY });
        }
      };

      window.addEventListener("mousemove", updateMousePosition);
      window.addEventListener("touchmove", updateMousePosition);

      // Clean up the event listeners on component unmount
      return () => {
        window.removeEventListener("mousemove", updateMousePosition);
        window.removeEventListener("touchmove", updateMousePosition);
      };
    }
  }, [active]);

  return position;
};